import type { ModuleReportData } from '../../providers/DeepSearchCompletion';
import { useEffect, useRef, useCallback } from 'react';
import { Body, Button, Modal, ModalBody, ModalHeader } from '@workramp/ui';
import { I18n } from '~/common/explicit-exports';
import { useActionCableSubscription } from '~/hooks';
import { ChatInterface, useChat } from '~/features/chat';
import { EmptyState } from '~/features/chat/components/ChatThread/ChatThread.styles';
import { useQuestionContext } from '../../hooks/useQuestionContext';
import { DeepSearchCompletionProvider, useDeepSearchCompletion } from '../../providers/DeepSearchCompletion';
import { useCreateGuideFromFileMutation } from '../../api';
import { useGenerateGuideStructure } from '../../hooks/useGenerateGuideStructure';

interface CreateDeepSearchModalProps {
  isOpen: boolean;
  initialName?: string;
  onClose: () => void;
}

const concatenateMarkdownReports = (reports: ModuleReportData[]): string =>
  reports.map((report) => report.content || '').join('\n\n');

const createMarkdownFile = (markdownContent: string, fileName: string): File => {
  const blob = new Blob([markdownContent], { type: 'text/markdown' });
  return new File([blob], fileName, { type: 'text/markdown' });
};

function InnerCreateDeepSearchModal({ isOpen, initialName, onClose }: CreateDeepSearchModalProps) {
  const isInitialized = useRef(false);

  const TRANSLATED_TITLE = I18n.t('js.admin.content_creation.create_modal_title', {
    content: 'Training',
  });

  const { unsubscribeAll } = useDeepSearchCompletion();

  const { messages, isLoading, pushMessage, setIsLoading, clearMessages } = useChat();

  const pushLoadingState = useCallback(() => {
    setIsLoading(true);
  }, [setIsLoading]);

  const stopLoadingState = useCallback(() => {
    setIsLoading(false);
  }, [setIsLoading]);

  const pushSystemMessage = useCallback(
    (message: string | React.ReactNode) => {
      pushMessage(message, false);
      stopLoadingState();
    },
    [pushMessage, stopLoadingState],
  );

  const pushUserMessage = useCallback(
    (message: string | React.ReactNode) => {
      pushMessage(message, true);
    },
    [pushMessage],
  );

  const createGuideFromFileMutation = useCreateGuideFromFileMutation();

  const handleCreateGuideFromReports = useCallback(
    (structure, reports) => {
      if (reports && reports.length > 0) {
        pushSystemMessage(<Body level="sm">We are working on creating the guide...</Body>);
        const markdownContent = concatenateMarkdownReports(reports);
        const markdownFile = createMarkdownFile(markdownContent, 'generated_guide.md');

        createGuideFromFileMutation.mutate({
          files: [markdownFile],
          title: structure.courseTitle || 'Generated Guide from Reports',
        });
      } else {
        pushSystemMessage(<Body level="sm">No reports available to create a guide.</Body>);
      }
    },
    [createGuideFromFileMutation, pushSystemMessage],
  );

  const { handleGenerateGuideStructure } = useGenerateGuideStructure({
    pushSystemMessage,
    pushLoadingState,
    stopLoadingState,
    onComplete: (structure, reports) => {
      pushSystemMessage(<Body level="sm">All module reports have been generated successfully!</Body>);
      pushSystemMessage(
        <Button
          onPress={() => {
            handleCreateGuideFromReports(structure, reports);
            pushLoadingState();
          }}
        >
          Create Guide
        </Button>,
      );
    },
  });

  const { handleUserMessage, placeholder, resetQuestions, currentSuggestions } = useQuestionContext({
    pushLoadingState,
    pushSystemMessage,
    onComplete: (data) => {
      handleGenerateGuideStructure(data.topic, data.questions, data.answers);
    },
  });

  const handleClose = useCallback(() => {
    unsubscribeAll();
    resetQuestions();
    onClose();
    isInitialized.current = false;
  }, [onClose, resetQuestions, unsubscribeAll]);

  useEffect(() => {
    if (initialName && !isInitialized.current && isOpen) {
      clearMessages();
      pushUserMessage(initialName);
      handleUserMessage(initialName);
      isInitialized.current = true;
    }
  }, [isOpen, initialName, pushUserMessage, handleUserMessage, clearMessages]);

  useActionCableSubscription(
    {
      channel: 'Courses::GenerationInitiatedChannel',
    },
    {
      received: (data) => {
        if (data.error) {
          pushSystemMessage(data.error);

          return;
        }

        window.location.href = `/admin/task_versions/${data.id}/preview`;
      },
    },
    {
      isEnabled: isLoading,
    },
  );
  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalHeader title={TRANSLATED_TITLE} />
      <ModalBody>
        <ChatInterface
          messages={messages}
          isSubmitting={isLoading}
          onUserMessage={(message) => {
            pushUserMessage(message);
            handleUserMessage(message);
          }}
          placeholder={placeholder}
          suggestions={currentSuggestions}
          emptyState={<EmptyState>What topic would you like to create a deep research for?</EmptyState>}
        />
      </ModalBody>
    </Modal>
  );
}

export function CreateGuideFromDeepSearchModal(props: CreateDeepSearchModalProps) {
  return (
    <DeepSearchCompletionProvider>
      <InnerCreateDeepSearchModal {...props} />
    </DeepSearchCompletionProvider>
  );
}
